import {
  BATTLE_TOWER_BOSS_HP,
  BATTLE_TOWER_CAPSTONE_BOSS_HP,
  BATTLE_TOWER_CAPSTONE_ZONE_INDEX,
  battleTowerTrialOffenseTypeMult,
  getBattleTowerZone,
  getBattleTowerBossRequiredDps,
  scaleBattleTowerTrialBossHp,
} from '~/data/battleTower'
import { getPokemonPowerBreakdown, resolvePowerBaseSlug } from '~/data/pokemonPower'
import { computeCombatEffectiveDps, toCombatDps } from '~/data/pokemonPower'
import { getSlugGeneration } from '~/data/gacha'
import type { PokemonType } from '~/data/types'
import { getPokemonTypes, getTypeEffectiveness } from '~/data/types'
import { getZone } from '~/data/zones'
import type { OwnedPokemon } from '~/stores/useInventoryStore'
import { computeZoneBossTotalHp, zoneBossMinTeamDps } from '~/utils/championBoss'

export type BossReadinessContext = {
  defenderTypes: PokemonType[]
  /** PV du boss (après scaling NG+ / mitigation). */
  scaledBossHp: number
  timerSeconds: number
  requiredDps: number
}

/** Options alignées sur le tick de combat (`useCombatLoop.getEffectiveDps`). */
export type TeamCombatDpsOpts = {
  battleTowerZone: number | null
  isEndgame: boolean
  combatGeneration: number | null
  currentGeneration: number
  zoneMasteryDpsMult: number
  pokedexDpsMult: number
  synergyDpsMult: number
}

export type TeamBossCombatDpsOpts = TeamCombatDpsOpts & {
  dpsPenaltyMult?: number
  ascensionDpsMult?: number
  combatPressureMult?: number
  /** Mult. DPS keystone — apparié au mult. PV ennemis keystone côté `requiredDps`. */
  keystoneDpsMult?: number
  /** Bonus permanents appliqués en combat réel — inclus ici pour que la jauge/auto-combat collent. */
  goldWellDpsMult?: number
  transcendenceDpsMult?: number
}

function resolveFrontierBossCoords(opts: {
  activeCombatGen: number
  activeCombatZone: number
  currentGeneration: number
  currentZone: number
  combatGeneration: number | null
  battleTowerZone: number | null
}): { gen: number; zone: number } {
  if (opts.battleTowerZone != null) {
    return { gen: opts.activeCombatGen, zone: opts.activeCombatZone }
  }
  if (opts.combatGeneration !== null) {
    return { gen: opts.activeCombatGen, zone: opts.activeCombatZone }
  }
  return { gen: opts.currentGeneration, zone: opts.currentZone }
}

/** DPS offensif d’un Pokémon vs types défenseur — même formule que `getPokeDps().effectiveDps`. */
export function computePokemonOffensiveDps(
  poke: OwnedPokemon,
  defenderTypes: PokemonType[],
  opts: TeamCombatDpsOpts,
): number {
  const power = getPokemonPowerBreakdown(poke)
  const pokeGen = getSlugGeneration(resolvePowerBaseSlug(poke.slug, poke.cosmeticFormSlug))
  const combatGen = opts.combatGeneration ?? opts.currentGeneration
  const regionMult = opts.isEndgame ? 1.0 : pokeGen >= combatGen ? 1.0 : 0.001
  const attackerTypes = getPokemonTypes(poke)
  let typeMult = 1
  if (defenderTypes.length > 0) {
    typeMult = Math.max(
      ...attackerTypes.map((atkType) => getTypeEffectiveness(atkType, defenderTypes)),
    )
  }
  typeMult = battleTowerTrialOffenseTypeMult(typeMult, opts.battleTowerZone)
  const permanentDps = Math.round(power.power * regionMult)
  return toCombatDps(permanentDps * typeMult)
}

/** DPS offensif équipe (bonus maîtrise / pokédex / synergie) — aligné `getEffectiveDps`. */
export function computeTeamOffensiveDps(
  team: OwnedPokemon[],
  defenderTypes: PokemonType[],
  opts: TeamCombatDpsOpts,
): number {
  if (team.length === 0) return 0
  let total = 0
  for (const poke of team) {
    total += computePokemonOffensiveDps(poke, defenderTypes, opts)
  }
  return toCombatDps(total * opts.zoneMasteryDpsMult * opts.pokedexDpsMult * opts.synergyDpsMult)
}

/** DPS effectif en combat boss (malus, ascension, pression). */
export function computeTeamBossCombatDps(
  team: OwnedPokemon[],
  defenderTypes: PokemonType[],
  opts: TeamBossCombatDpsOpts,
): number {
  const base = computeTeamOffensiveDps(team, defenderTypes, opts)
  if (base <= 0) return 0
  return computeCombatEffectiveDps(base, {
    dpsPenaltyMult: opts.dpsPenaltyMult,
    ascensionDpsMult: opts.ascensionDpsMult,
    combatPressureMult: opts.combatPressureMult,
    keystoneDpsMult: opts.keystoneDpsMult,
    goldWellDpsMult: opts.goldWellDpsMult,
    transcendenceDpsMult: opts.transcendenceDpsMult,
  })
}

export function isBossBattableByTeam(
  team: OwnedPokemon[],
  defenderTypes: PokemonType[],
  requiredDps: number,
  opts: TeamBossCombatDpsOpts,
): boolean {
  if (requiredDps <= 0 || team.length === 0) return false
  return computeTeamBossCombatDps(team, defenderTypes, opts) >= requiredDps
}

/** Alias rétrocompatible — préférer `computeTeamOffensiveDps`. */
export function teamOffensiveDpsVsDefender(
  team: OwnedPokemon[],
  defenderTypes: PokemonType[],
  opts: TeamCombatDpsOpts,
): number {
  return computeTeamOffensiveDps(team, defenderTypes, opts)
}

export function resolveBossReadinessContext(opts: {
  activeCombatGen: number
  activeCombatZone: number
  currentGeneration: number
  currentZone: number
  combatGeneration: number | null
  battleTowerZone: number | null
  ascensionLevel: number
  enemyMitigationRanks: number
  /** Mult. PV boss routes (NG+) — aligné `spawnBoss` / `player.ascensionEnemyHpMult`. */
  ascensionEnemyHpMult: number
  /** Mult. PV ennemis keystone (1 si aucun keystone PV attuné). Appliqué routes ET tour. */
  keystoneEnemyHpMult?: number
}): BossReadinessContext | null {
  const ksHp = Math.max(0, Number(opts.keystoneEnemyHpMult ?? 1)) || 1
  const towerZone = opts.battleTowerZone
  if (towerZone != null && towerZone >= 1 && towerZone <= BATTLE_TOWER_CAPSTONE_ZONE_INDEX) {
    const z = getBattleTowerZone(towerZone)
    const boss = z?.boss
    if (!boss) return null
    const types = boss.types ?? ['normal']
    const timerSec = boss.timerSeconds ?? 30
    const baseHp =
      towerZone === BATTLE_TOWER_CAPSTONE_ZONE_INDEX
        ? BATTLE_TOWER_CAPSTONE_BOSS_HP
        : BATTLE_TOWER_BOSS_HP
    // Keystone PV appliqué aussi en tour (aligné `spawnBattleTowerBossCombat`).
    const scaledBossHp = Math.max(
      1,
      Math.round(
        scaleBattleTowerTrialBossHp(baseHp, opts.ascensionLevel, opts.enemyMitigationRanks) * ksHp,
      ),
    )
    return {
      defenderTypes: types,
      scaledBossHp,
      timerSeconds: timerSec,
      requiredDps: Math.ceil(
        getBattleTowerBossRequiredDps(
          baseHp,
          timerSec,
          opts.ascensionLevel,
          opts.enemyMitigationRanks,
        ) * ksHp,
      ),
    }
  }

  const { gen, zone } = resolveFrontierBossCoords(opts)
  const zoneDef = getZone(gen, zone)
  const boss = zoneDef?.boss
  if (!boss) return null
  const types = boss.types ?? ['normal']
  const timerSec = boss.timerSeconds ?? 30
  const hpMult = Math.max(1, opts.ascensionEnemyHpMult) * (opts.keystoneEnemyHpMult ?? 1)
  const scaledBossHp = computeZoneBossTotalHp(gen, zone, hpMult)
  const requiredDps = zoneBossMinTeamDps(gen, zone, timerSec, hpMult)
  return {
    defenderTypes: types,
    scaledBossHp,
    timerSeconds: timerSec,
    requiredDps,
  }
}
